/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, FieldArray, yupToFormErrors } from 'formik'
import { DatePicker } from '@mdsol/lego'
import { useEsignature } from '../../../../hooks/useEsignature'
import { TextArea } from '@mdsol/lego'
import Modal from '../../../../components/Modal'
import DocTypeDropdown from '../../../../components/pickers/DocTypeDropdown'
import { Select } from '../../../../components/Select'
import UserGroupSteps from '../../../../components/workflow/UserGroupSteps'
import { formatTextAreaCounter, transformToSelectOptions } from '../../../../utils/formHelpers'
import ValidationSchema from './FormValidation'
import { ErrorMessageStyled } from '~/components/shared/ErrorMessage'
import Field from '~/components/shared/Field'
import AddCustomJustificationModal from '../../../../components/modals/AddCustomJustificationModal'
import { useWorkflowCreation } from '../../../../hooks/useWorkflowCreation'
import {
  getFile,
  getFolderFolderType,
  getFileCRApprovers,
  getFsiEditors,
  getFileReviewers,
  getFileApprovers
} from '../../../../services/api/files'
import { getJustifications } from '../../../../services/api/api.settings'
import { toast } from '../../../../services/toast'
import { documentTypesApiService } from '../../../../services/api/api.documenttypes'
import { changeRequestTypesApiService } from '../../../../services/api/api.changerequesttypes'
import { WorkflowType, WorkflowProcessingType } from '../../../../constants/workflow'
import { SystemFolderType } from '../../../../constants/fsi'
import Constants from '../../../../constants/constants'
import type {
  AddDocumentModalProps,
  FormValues,
  RequestData,
  RequestParams,
  SelectOption,
  DocumentTypeObject,
  WorkflowTypeData
} from './AddDocumentModal.types'
import useWorkflowServiceNew from '~/services/workflow/useWorkflowServiceNew'
import LoadingSpinner from '~/components/LoadingSpinner'
import { getMomentDateFormat, getDatePickerParseFormats } from '~/utils/dateFormatting'

/**
 * Add Document Modal Component
 * Migrated from lego/page/browser/add-document/add-new-document/AddDocumentModal.jsx
 *
 * Handles multiple workflow types:
 * - Regular document creation
 * - Amendment addition
 * - File plan creation
 * - Document reclassification
 * - File to eTMF direct
 */

export const AddDocumentModal: React.FC<AddDocumentModalProps> = (props) => {
  const { t: translate } = useTranslation()
  const { createCr } = useWorkflowServiceNew()

  const [isOpen, setIsOpen] = useState(true)
  const [requestParams, setRequestParams] = useState<any>({})
  const [availableJustifications, setAvailableJustifications] = useState<SelectOption[]>([])
  const [allowedDocTypes, setAllowedDocTypes] = useState<number[] | null>(null)
  const [showAddCustomModal, setShowAddCustomModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  const validationSchema = ValidationSchema()

  const [request, setRequest] = useState<RequestData>({
    type: WorkflowType.NEW,
    parentId: props.parentId,
    justificationTextId: null,
    collaborators: [{ processingType: WorkflowProcessingType.SERIAL, merged: [] }],
    approver: {}
  })

  const [parentFolder, setParentFolder] = useState<any>({})
  const [isFileToEtmfOrReclassifyAndSourceFileOwner, setIsFileToEtmfOrReclassifyAndSourceFileOwner] = useState(false)
  const [selectedDocTypeObj, setSelectedDocTypeObj] = useState<DocumentTypeObject>({} as DocumentTypeObject)
  const [initialFormValues, setInitialFormValues] = useState<FormValues>({
    crdoctype: null,
    expiresAtDate: null,
    fileName: '',
    justificationTextId: null,
    description: '',
    selectedDocTypeObj: {} as DocumentTypeObject
  })
  const [descriptionPlaceHolder, setDescriptionPlaceHolder] = useState('')
  const formRef = useRef<any>(null)
  const isInitializing = useRef(false)
  const [isLoadingDocType, setIsLoadingDocType] = useState(false)

  useEffect(() => {
    // Prevent multiple initializations
    if (!isInitializing.current) {
      isInitializing.current = true
      initializeModal()
    }
  }, []) // Intentionally empty - should only run once on mount

  const initializeModal = async () => {
    setIsInitialLoading(true)
    const requestData = { ...request }
    const requestParamsData: RequestParams =
      props.filePlan && props.docId
        ? (props as unknown as RequestParams)
        : typeof props.params === 'object'
          ? props.params
          : {}

    setRequestParams(requestParamsData)
    setIsFileToEtmfOrReclassifyAndSourceFileOwner(
      (requestParamsData.reclassify || requestParamsData.fileToEtmf) &&
        requestParamsData.file &&
        (requestParamsData.file?.ownerId || requestParamsData.file?.owner?.id)
    )

    // Set default description placeholder
    let defaultDescription = ''
    if (requestParamsData.reclassify) {
      defaultDescription = translate('CR_DESC_DEFAULT_PLACEHOLDER_RECLASSIFY_AS_NEW_DOC')
    } else if (requestParamsData.fileToEtmf) {
      defaultDescription = translate('CR_DESC_DEFAULT_PLACEHOLDER_FILE_TO_ETMF_DIRECT')
    } else {
      defaultDescription = translate('CR_DESC_DEFAULT_PLACEHOLDER_ADD')
    }

    setDescriptionPlaceHolder(defaultDescription)
    requestData.description = defaultDescription
    setInitialFormValues((old) => ({ ...old, description: defaultDescription }))

    // Load justifications
    try {
      const justificationsData = await getJustifications('en-US')
      const elements = (justificationsData as any)?.elements || []
      elements.push({ id: 0, text: translate('CUSTOM...') })
      setAvailableJustifications(transformToSelectOptions(elements, 'id', 'text'))
    } catch (error) {
      console.error('Failed to load justifications:', error)
    }

    // Load parent folder
    try {
      const parentFolderData = await getFile(props.parentId)
      setParentFolder(parentFolderData)
      requestData.parentFolderName = parentFolderData.name
      setRequest(requestData)

      if (requestParamsData.filePlan && requestParamsData.docId) {
        assembleAllowedDocTypes({}, requestParamsData.docId)
      } else {
        if (
          (parentFolderData as any).systemFolderType === SystemFolderType.REGULATED_CONTENT ||
          (parentFolderData as any).templateId
        ) {
          const parentId = requestParamsData.amend ? (parentFolderData as any).parentId : props.parentId

          try {
            const folderTypeData = await getFolderFolderType(parentId)

            if (
              folderTypeData.documentTypesAllowed ===
                Constants.system.settings.folderType.documentTypesAllowed.DEFINED_ON_FOLDER &&
              !folderTypeData.allowedDocumentTypeTemplateIds.length &&
              requestParamsData.fileToEtmf
            ) {
              // Show error toast
              toast.warning(translate('FILE_TO_ETMF_ADD_DOC_NO_DEFINED_DOC_TYPES_ERROR_MSG'))
              doClose()
            }

            assembleAllowedDocTypes(folderTypeData)
          } catch (error) {
            console.error('Failed to load folder type:', error)
          }
        } else {
          assembleAllowedDocTypes({
            documentTypesAllowed: Constants.system.settings.folderType.documentTypesAllowed.ALL
          })
        }
      }
    } catch (error) {
      console.error('Failed to load parent folder:', error)
    } finally {
      setIsInitialLoading(false) // Stop loading
    }
  }

  const assembleAllowedDocTypes = (folderTypeData: any, filePlanId?: number) => {
    let allowedDocTypesData: number[] | null = null
    if (filePlanId) {
      allowedDocTypesData = [filePlanId]
    } else if (folderTypeData.documentTypesAllowed !== Constants.system.settings.folderType.documentTypesAllowed.ALL) {
      allowedDocTypesData = folderTypeData.allowedDocumentTypeTemplateIds
    }
    setAllowedDocTypes(allowedDocTypesData)
  }

  const isMultiple = (): boolean => {
    return !!(requestParams?.selectedIds && requestParams.selectedIds.length > 1)
  }

  const docTypeChanged = useCallback(
    async (selectedDocType: SelectOption) => {
      if (selectedDocType?.value) {
        // Prevent multiple simultaneous calls
        if (isLoadingDocType) {
          return
        }

        setIsLoadingDocType(true)

        setInitialFormValues((oldData) => ({
          ...oldData,
          crdoctype: selectedDocType,
          fileName: formRef.current?.values?.fileName || '',
          description: formRef.current?.values?.description || descriptionPlaceHolder
        }))

        setRequest((old) => ({ ...old, documentTypeTemplateId: selectedDocType as any }))

        try {
          const data = await documentTypesApiService.getById(Number(selectedDocType.value), false)
          const selectedDocTypeObjData = data as DocumentTypeObject
          setSelectedDocTypeObj(selectedDocTypeObjData)
          await downloadAllTaskGroupSteps(selectedDocTypeObjData)
        } catch (error) {
          console.error('Failed to load document type:', error)
          toast.error(translate('FAILED_TO_LOAD_DOCUMENT_TYPE'))
        } finally {
          setIsLoadingDocType(false)
        }
      }
    },
    [descriptionPlaceHolder, isFileToEtmfOrReclassifyAndSourceFileOwner, props.parentId, translate]
  )

  const downloadAllTaskGroupSteps = async (selectedDocTypeObjData: DocumentTypeObject) => {
    let typeData = {} as WorkflowTypeData

    if (selectedDocTypeObjData?.workflowTemplate?.id) {
      try {
        const data = await changeRequestTypesApiService.get(selectedDocTypeObjData.workflowTemplate.id)
        const fetchedTypeData = await changeRequestTypesApiService.assembleForUI(
          data,
          isFileToEtmfOrReclassifyAndSourceFileOwner ? false : true
        )
        typeData = fetchedTypeData as WorkflowTypeData
        setInitialFormValues((oldData) => ({ ...oldData, typeData }))
      } catch (error) {
        doClose()
      }
    } else {
      try {
        const [initialApprovers, collaborators, reviewers, approvers] = await Promise.all([
          getFileCRApprovers(props.parentId, true),
          getFsiEditors(props.parentId, isFileToEtmfOrReclassifyAndSourceFileOwner ? false : true),
          getFileReviewers(props.parentId),
          getFileApprovers(props.parentId)
        ])

        typeData = {
          initialApprovers: (initialApprovers as any).initialApprovers,
          collaborators: (collaborators as any).collaborators,
          reviewers: (reviewers as any).reviewers,
          approvers: (approvers as any).approvers
        }

        setInitialFormValues((oldData) => ({ ...oldData, typeData }))
      } catch (error) {
        // Legacy parity: non-template workflow-step loading failures are silent.
        console.error('Failed to load workflow steps:', error)
      }
    }
  }

  const downloadFile = async (fileId: number) => {
    try {
      toast.info(translate('BROWSER_DOWNLOAD_IS_PREPARING'))

      // Validate file first (calls /api/files/{id}/validate)
      const { validateFile, getFileDownloadUrl } = await import('../../../../services/api/files')
      const validationResult = await validateFile(fileId)

      if (validationResult.value === true) {
        // File is valid, proceed with download using original file URL (not PDF conversion)
        const downloadInfo = await getFileDownloadUrl(fileId)

        if (downloadInfo.url) {
          // Create download link and trigger download
          const link = document.createElement('a')
          link.href = downloadInfo.url
          link.download = parentFolder?.name || `file-${fileId}`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        } else if (downloadInfo.message) {
          toast.info(downloadInfo.message)
        }
      } else {
        toast.error(translate('BROWSER_VALIDATE_FILE_IS_INVALID'))
      }
    } catch (error: any) {
      toast.error(translate(error))
    }
  }

  const save = async (values: FormValues) => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return
    }

    const modifiedValues = { ...values }

    if (modifiedValues.expiresAtDate) {
      // Convert date picker value to Date object like legacy
      ;(modifiedValues as any).expirationDate =
        modifiedValues.expiresAtDate instanceof Date
          ? modifiedValues.expiresAtDate
          : new Date(modifiedValues.expiresAtDate)
    }

    if (request.justificationTextId) {
      ;(modifiedValues as any).justificationTextId = { value: request.justificationTextId.value }
    }

    const typeData = modifiedValues.typeData

    // Manual validation like legacy code
    if (!formRef.current) {
      return
    }

    try {
      const errors = await formRef.current.validateForm()

      if (Object.keys(errors || {}).length > 0) {
        toast.error(translate('PLEASE_FIX_VALIDATION_ERRORS'))
        return
      }

      setIsSubmitting(true)

      const finalRequest = await assembleData(request)

      await createCr({
        req: { ...finalRequest, ...modifiedValues },
        type: typeData
      })
      window.dispatchEvent(new CustomEvent('updateFiles'))
      doClose()
    } catch (error: any) {
      toast.error(error?.body?.message || error?.message || translate(error))
      setIsSubmitting(false)
    }
  }

  const assembleData = async (requestData: RequestData): Promise<RequestData> => {
    const result = { ...requestData }

    if (requestParams?.amend) {
      Object.assign(result, {
        parentId: parentFolder.parentId,
        isAmendmentAddition: true,
        fileIds: [props.parentId]
      })
    } else if (requestParams?.reclassify) {
      Object.assign(result, {
        type: WorkflowType.RECLASSIFY_DOCUMENT,
        targetFsiId: props.parentId,
        fileIds: [requestParams.file?.id]
      })
    } else if (requestParams?.fileToEtmf) {
      Object.assign(result, {
        type: WorkflowType.FILE_ARTIFACT,
        targetFsiId: props.parentId,
        fileIds: [requestParams.file?.id],
        obsoleteSourceDocumentUponApproval: requestParams.obsolete
      })

      if (isMultiple()) {
        result.fileIds = null
        delete result.fileId
        result.sourceFiles = requestParams.selectedIds.map((id: number) => ({
          fsiId: id,
          title: (result as any).fileName,
          expirationDate:
            (result as any).expirationDate && (result as any).expirationDate instanceof Date
              ? (result as any).expirationDate.getTime()
              : null
        }))
      }
    }

    return result
  }

  const doClose = () => {
    setIsOpen(false)
    if (props.onClose) {
      props.onClose()
    }
  }

  const getModalTitle = (): string => {
    if (requestParams?.amend) {
      return translate('BROWSER_ADD_AMEND_DOCUMENT_H_TITLE')
    } else if (requestParams?.filePlan) {
      return translate('BROWSER_ADD_NEW_FILEPLAN_H_TITLE')
    } else if (requestParams?.reclassify) {
      return translate('BROWSER_RECLASSIFY_AS_NEW_DOC_H_TITLE')
    } else if (requestParams?.fileToEtmf) {
      return translate('BROWSER_FILE_DOC_TO_ETMF_DIRECT_H_TITLE')
    } else {
      return translate('BROWSER_ADD_REGULAR_DOCUMENT_H_TITLE')
    }
  }

  return (
    <div>
      <Modal
        show={isOpen}
        handleClose={doClose}
        size="lg"
        dataTestId="modal-add-file"
        id="modal-add-file"
        className="add-doc-modal-wrapper theme-sandman"
        title={getModalTitle()}
        loading={isSubmitting || isLoadingDocType}
      >
        <Formik
          innerRef={formRef}
          initialValues={{ ...initialFormValues, selectedDocTypeObj }}
          validationSchema={validationSchema}
          onSubmit={save}
          enableReinitialize
        >
          {({ values, setFieldValue, handleSubmit }) => (
            <>
              <Modal.Body>
                {isInitialLoading ? (
                  <LoadingSpinner size={40} centered={true} />
                ) : (
                  <div>
                    {requestParams?.amend && (
                      <div className="form-group md-form-group wparag w100p">
                        <label>{translate('PARENT_DOCUMENT')}</label>
                        <p>{requestParams?.file?.name}</p>
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="0e64f7ca-03f6-47d0-86af-c93a29572ef0" className="fs12 mb0">
                        {translate('DOC_TYPE')}
                      </label>
                      <div className={`${selectedDocTypeObj?.templateFile ? 'd-flex justify-content-start' : ''}`}>
                        <div className="w50p">
                          <DocTypeDropdown
                            placeholder="SELECT_DOC_TYPE_PLACEHOLDER"
                            name="crdoctype"
                            isSingle
                            required
                            id="0e64f7ca-03e6-47d0-86gf-c93a29572ef0"
                            allowedTypes={allowedDocTypes || undefined}
                            value={values.crdoctype}
                            onChange={(option: any) => {
                              docTypeChanged(option)
                            }}
                          />
                          <ErrorMessageStyled name="crdoctype" />
                        </div>
                        {selectedDocTypeObj?.templateFile && (
                          <div className="pos-relative">
                            <a
                              className="download-link next-to-select ml30"
                              onClick={() => downloadFile(selectedDocTypeObj.templateFile!.id)}
                            >
                              {translate('DOWNLOAD_TEMPLATE')}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedDocTypeObj?.requireJustification && (
                      <div className="form-group w50p">
                        <label htmlFor="i-jtype" className="fs12 mb0">
                          {translate('JUSTIFICATION')}
                        </label>
                        <div id="17e6669a-57a5-428a-a0ac-271d6f321ea3">
                          <Select
                            id="i-jtype"
                            name="justificationTextId"
                            placeholder={translate('SELECT_JUSTIFICATION_LABEL')}
                            isClearable={false}
                            value={values.justificationTextId}
                            required
                            options={availableJustifications}
                            onChange={(option: any) => {
                              setFieldValue('justificationTextId', option ? option.value : null)
                              setRequest((previousData) => ({
                                ...previousData,
                                justificationTextId: option
                              }))
                            }}
                          />
                          <ErrorMessageStyled name="justificationTextId" />
                        </div>
                        {values.justificationTextId === 0 && (
                          <button
                            type="button"
                            className="btn btn-chita mt10"
                            onClick={() => setShowAddCustomModal(true)}
                          >
                            {translate('DESCRIPTION')}
                          </button>
                        )}
                      </div>
                    )}

                    <div id="e8eebc6c-615a-44ed-8fda-3083565935b3" className="description-wrapper form-group">
                      <label htmlFor="in-f" className="fs12 mb0">
                        {translate('BROWSER_SEND_RQST_REASON_LABEL')}
                      </label>
                      <div id="c8ecc3fb-bf24-42d7-8154-4c24d2d11f0d" className="w50p">
                        <TextArea
                          maxLength={254}
                          name="description"
                          onChange={(event: any) => {
                            const { value } = event.target
                            setFieldValue('description', value)
                          }}
                          id="in-f"
                          value={values?.description}
                          rows={6}
                        />
                        <ErrorMessageStyled name="description" />
                      </div>
                    </div>

                    {!selectedDocTypeObj?.autonamingEnabled && (
                      <div className="form-group w50p">
                        <label className="fs12 mb0">
                          {translate('FILENAME_WITHOUT_EXT_LABEL')}{' '}
                          {!props.filePlan && <span className="required">{translate('(OPTIONAL)')}</span>}
                        </label>
                        <div id="575f0a3c-79e2-419d-8879-100016696f2d">
                          <Field
                            type="text"
                            name="fileName"
                            maxLength={234}
                            id="8831e0c7-5018-417a-8326-c8a85d15a590"
                            className="w100p"
                          />
                          <ErrorMessageStyled name="fileName" />
                        </div>
                      </div>
                    )}

                    {selectedDocTypeObj?.autonamingEnabled && (
                      <div className="form-group w50p">
                        <label className="fs12 mb0">{translate('DOCUMENT_TITLE')}</label>
                        <div id="d4ad4487-bc18-4d1f-97ab-f717d1f50b0b">
                          <Field
                            type="text"
                            name="fileName"
                            maxLength={255}
                            id="07eb3d2b-1081-4b21-ab10-3e0797547704"
                            className="w100p"
                          />
                          <ErrorMessageStyled name="fileName" />
                        </div>
                      </div>
                    )}

                    {selectedDocTypeObj?.requireExpirationDate && (
                      <div className="form-group w50p">
                        <label className="fs12 mb0">{translate('BROWSER_ADD_DOCUMENT_EXPIRATION_DATE_LABEL')}</label>
                        <div id="3611cdd4-ce33-4ace-ac76-3635423ee8e2">
                          <DatePicker
                            placeholderText={translate('-SELECT_DATE-')}
                            name="expiresAtDate"
                            onChange={(val: any) => {
                              setFieldValue('expiresAtDate', val)
                            }}
                            popperProps={{ positionFixed: true }}
                            selected={values.expiresAtDate}
                            popoverTargetOffset="3px 0"
                            todayButton="Today"
                            dateFormatCalendar="MMMM"
                            dateFormat={getDatePickerParseFormats()}
                            showYearDropdown
                            className="form-control field-control w100p"
                            id="77ea2daa-2cdc-4a98-8c8d-55386794db7b"
                          />
                          <ErrorMessageStyled name="expiresAtDate" />
                        </div>
                      </div>
                    )}

                    {values?.typeData?.initialApprovers?.type === 'SETUP_WITH_WORKFLOW' &&
                      !requestParams.reclassify &&
                      !requestParams.fileToEtmf && (
                        <div>
                          <FieldArray name="typeData.initialApprovers.steps">
                            {(arrayHelpers) => (
                              <UserGroupSteps
                                arrayHelpers={arrayHelpers}
                                values={values?.typeData?.initialApprovers?.steps || []}
                                nofallTxt="BROWSER_FILE_SETTINGS_CR_APPROVERS_INFO_LABEL"
                                labelTxt="BROWSER_FILE_SETTINGS_CR_APPROVERS_APPROVAL_SEQUENCE_HEADLINE"
                                increaseUserGroupWidth
                              />
                            )}
                          </FieldArray>
                        </div>
                      )}

                    {values?.typeData?.collaborators?.type === 'SETUP_WITH_WORKFLOW' && (
                      <div>
                        <FieldArray name="typeData.collaborators.steps">
                          {(arrayHelpers) => (
                            <UserGroupSteps
                              arrayHelpers={arrayHelpers}
                              values={values?.typeData?.collaborators?.steps || []}
                              nofallTxt="BROWSER_FILE_SETTINGS_EDITORS_INFO_LABEL"
                              labelTxt="BROWSER_FILE_SETTINGS_EDITORS_SEQUENCE_HEADLINE"
                              autoAddUser={
                                isFileToEtmfOrReclassifyAndSourceFileOwner
                                  ? requestParams.file.ownerId || requestParams.file.owner?.id
                                  : false
                              }
                              increaseUserGroupWidth
                            />
                          )}
                        </FieldArray>
                      </div>
                    )}

                    {values?.typeData?.reviewers?.type === 'SETUP_WITH_WORKFLOW' && (
                      <div>
                        <FieldArray name="typeData.reviewers.steps">
                          {(arrayHelpers) => (
                            <UserGroupSteps
                              arrayHelpers={arrayHelpers}
                              values={values?.typeData?.reviewers?.steps || []}
                              nofallTxt="BROWSER_FILE_SETTINGS_REVIEWERS_INFO_LABEL"
                              labelTxt="BROWSER_FILE_SETTINGS_REVIEWERS_REVIEW_SEQUENCE_HEADLINE"
                              increaseUserGroupWidth
                            />
                          )}
                        </FieldArray>
                      </div>
                    )}

                    {values?.typeData?.approvers?.type === 'SETUP_WITH_WORKFLOW' && (
                      <div>
                        <FieldArray name="typeData.approvers.steps">
                          {(arrayHelpers) => (
                            <UserGroupSteps
                              arrayHelpers={arrayHelpers}
                              values={values?.typeData?.approvers?.steps || []}
                              nofallTxt="BROWSER_FILE_SETTINGS_APPROVERS_INFO_LABEL"
                              labelTxt="BROWSER_FILE_SETTINGS_APPROVERS_APPROVAL_SEQUENCE_HEADLINE"
                              increaseUserGroupWidth
                            />
                          )}
                        </FieldArray>
                      </div>
                    )}
                  </div>
                )}
              </Modal.Body>

              <Modal.Footer>
                <button
                  className="btn btn-close"
                  type="button"
                  onClick={doClose}
                  id="68b5d13b-4b96-43cc-9388-644aba07502f"
                >
                  {translate('CANCEL')}
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    handleSubmit()
                  }}
                  disabled={isSubmitting}
                  id="8e34de50-c253-482a-8490-dac5550c2351"
                >
                  {isSubmitting ? translate('SUBMITTING...') : translate('BROWSER_SEND_REQUEST')}
                </button>
              </Modal.Footer>
              {showAddCustomModal && (
                <AddCustomJustificationModal
                  request={request}
                  setRequest={setRequest}
                  setShowAddCustomModal={setShowAddCustomModal}
                />
              )}
            </>
          )}
        </Formik>
      </Modal>
    </div>
  )
}

export default AddDocumentModal
